import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Columns3, FilterX, MoreVertical, Plus, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import Paginator from "@/components/common/Paginator";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsButton from "@/components/table/ColumnSettingsButton";
import DataTable from "@/components/table/DataTable";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";
import {
  deletePartner,
  listPartners,
  updatePartner,
  type Partner,
  type PartnerInput,
} from "./api";
import {
  buildPartnerColumns,
  PARTNER_ROLE_LABEL,
  partnerColumnIds,
  partnerPinnedColumnIds,
  type PartnerField,
  type PartnerRole,
} from "./columns";
import PartnerCreateDialog from "./PartnerCreateDialog";
import { PARTNERS_PAGE_SIZE, usePartnerListParams } from "./usePartnerListParams";

const TRADING_OPTIONS = [
  { key: "all", label: "すべて" },
  { key: "active", label: "取引中" },
  { key: "stopped", label: "取引停止" },
];

// 用途の絞り込み。得意先と直送先は兼ねられるので、片方を選んでも
// もう一方を兼ねる相手は残る（「得意先」は is_client=true だけで引く）。
const ROLE_OPTIONS = [
  { key: "all", label: "すべて" },
  { key: "client", label: "得意先" },
  { key: "ship_to", label: "直送先" },
];

/**
 * 取引先マスタ。
 *
 * ngtg_msc の取引先画面と同じく、セルをクリックして 1 項目だけ編集する。
 * 編集ダイアログ・確認ダイアログはこの画面が 1 つずつ持ち、対象だけ差し替える
 * （セルごとに Modal を持たせると行数 × 列数ぶんの Modal が生まれるため）。
 */
export default function PartnerList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const editable = canWrite(user, "master");
  const {
    params,
    update,
    reset: resetParams,
    disabledParam,
    isClientParam,
    isShipToParam,
  } = usePartnerListParams();
  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings("partners", partnerColumnIds, partnerPinnedColumnIds);
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [creating, setCreating] = useState(false);
  // 編集中の 1 項目（セルをクリックすると入る）。
  const [editing, setEditing] = useState<{
    partner: Partner;
    field: PartnerField;
  } | null>(null);
  const [toggling, setToggling] = useState<Partner | null>(null);
  // 用途の切り替え（得意先 / 直送先）。どちらを触るかを添えて 1 つの確認で扱う。
  const [togglingRole, setTogglingRole] = useState<{
    partner: Partner;
    role: PartnerRole;
  } | null>(null);
  const [deleting, setDeleting] = useState<Partner | null>(null);

  const query = useQuery({
    queryKey: ["partners", params],
    queryFn: () =>
      listPartners({
        keyword: params.keyword || undefined,
        disabled: disabledParam,
        isClient: isClientParam,
        isShipTo: isShipToParam,
        sortKey: params.sortKey,
        ascending: params.ascending,
        limit: PARTNERS_PAGE_SIZE,
        offset: (params.page - 1) * PARTNERS_PAGE_SIZE,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["partners"] });

  const save = async (
    partner: Partner,
    input: PartnerInput,
  ): Promise<string | null> => {
    try {
      await updatePartner(partner.partner_id, input);
      await invalidate();
      return null;
    } catch (e) {
      // 入力し直せば解決する失敗（重複コードなど）はダイアログ内に返す。
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const toggle = useMutation({
    mutationFn: (partner: Partner) =>
      updatePartner(partner.partner_id, { disabled: !partner.disabled }),
    onSuccess: (partner) => {
      showSuccess(
        `「${partner.partner_name}」を${partner.disabled ? "取引停止" : "取引中"}にしました。`,
      );
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setToggling(null),
  });

  const toggleRole = useMutation({
    mutationFn: ({ partner, role }: { partner: Partner; role: PartnerRole }) =>
      updatePartner(partner.partner_id, { [role]: !partner[role] }),
    onSuccess: (partner, { role }) => {
      showSuccess(
        `「${partner.partner_name}」を${PARTNER_ROLE_LABEL[role]}として${
          partner[role] ? "使う" : "使わない"
        }設定にしました。`,
      );
      void invalidate();
    },
    // 両方を外そうとすると API が 422 で弾く（どの選択肢にも出ない取引先に
    // なってしまうため）。理由はそのまま出す。
    onError: (e: Error) => showError(e.message),
    onSettled: () => setTogglingRole(null),
  });

  const remove = useMutation({
    mutationFn: (partner: Partner) => deletePartner(partner.partner_id),
    onSuccess: (_, partner) => {
      showSuccess(`「${partner.partner_name}」を削除しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeleting(null),
  });

  const columns = useMemo(
    () =>
      buildPartnerColumns({
        canWrite: editable,
        onEditField: (partner, field) => setEditing({ partner, field }),
        onToggleDisabled: setToggling,
        onToggleRole: (partner, role) => setTogglingRole({ partner, role }),
        onDelete: setDeleting,
      }),
    [editable],
  );

  // 並び替えは API 側で行う（manualSorting）。列定義の meta.sortKey を使う。
  const sorting: SortingState = [{ id: params.sortKey, desc: !params.ascending }];

  const table = useReactTable<Partner>({
    data: query.data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.partner_id),
    columnResizeMode: "onChange",
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      const sortKey = table.getColumn(first.id)?.columnDef.meta?.sortKey;
      if (!sortKey) return;
      update({ sortKey, ascending: !first.desc });
    },
    enableSorting: true,
    defaultColumn: { enableSorting: false, minSize: 60, maxSize: 600 },
  });

  // 列ごとの並び替え可否は meta.sortKey の有無で決まる。
  for (const column of table.getAllLeafColumns()) {
    column.columnDef.enableSorting = Boolean(column.columnDef.meta?.sortKey);
  }

  const total = query.data?.pagination.total ?? 0;

  const editTarget: SingleFieldTarget | null = editing
    ? {
        label: editing.field.label,
        value: (editing.partner[editing.field.key] as string | null) ?? null,
        required: editing.field.required,
        kind: editing.field.kind ?? "text",
        options: editing.field.options,
        description: editing.field.description,
      }
    : null;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="取引先"
            actions={
              <>
                <Input
                  aria-label="取引先コード・名称・カナで検索"
                  placeholder="コード・名称・カナ"
                  variant="bordered"
                  radius="sm"
                  className="w-60"
                  startContent={<Search size={16} className="text-default-400" />}
                  defaultValue={params.keyword}
                  onBlur={(e) =>
                    update({ keyword: (e.target as HTMLInputElement).value.trim() })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      update({ keyword: (e.target as HTMLInputElement).value.trim() });
                    }
                  }}
                  isClearable
                  onClear={() => update({ keyword: "" })}
                />
                <Select
                  aria-label="取引状態で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-44"
                  disallowEmptySelection
                  selectedKeys={[params.trading]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as string | undefined;
                    if (value === "active" || value === "stopped" || value === "all") {
                      update({ trading: value });
                    }
                  }}
                  renderValue={(items) => {
                    const key = items[0]?.key as string | undefined;
                    const label =
                      TRADING_OPTIONS.find((o) => o.key === key)?.label ?? "すべて";
                    return `取引：${label}`;
                  }}
                  classNames={{
                    trigger: params.trading !== "all" ? "border-brand-500" : "",
                    value: params.trading !== "all" ? "font-bold text-brand-500" : "",
                  }}
                >
                  {TRADING_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <Select
                  aria-label="用途で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-40"
                  disallowEmptySelection
                  selectedKeys={[params.role]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as string | undefined;
                    if (value === "client" || value === "ship_to" || value === "all") {
                      update({ role: value });
                    }
                  }}
                  renderValue={(items) => {
                    const key = items[0]?.key as string | undefined;
                    const label =
                      ROLE_OPTIONS.find((o) => o.key === key)?.label ?? "すべて";
                    return `用途：${label}`;
                  }}
                  classNames={{
                    trigger: params.role !== "all" ? "border-brand-500" : "",
                    value: params.role !== "all" ? "font-bold text-brand-500" : "",
                  }}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <AuthorizedButton
                  scope="master"
                  radius="full"
                  color="primary"
                  startContent={<Plus size={18} />}
                  onPress={() => setCreating(true)}
                >
                  新規登録
                </AuthorizedButton>
                <ColumnSettingsButton table={table} onReset={resetColumns} />

                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      radius="full"
                      aria-label="表示設定"
                    >
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="表示設定"
                    onAction={(key) => {
                      if (key === "reload") void query.refetch();
                      if (key === "reset_filters") resetParams();
                      if (key === "reset_columns") resetColumns();
                    }}
                  >
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    <DropdownItem
                      key="reset_filters"
                      startContent={<FilterX size={18} />}
                    >
                      検索条件リセット
                    </DropdownItem>
                    <DropdownItem
                      key="reset_columns"
                      startContent={<Columns3 size={18} />}
                    >
                      表示列リセット
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />

          <div className="min-h-0 flex-1">
            {query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <p className="text-default-600">
                  データの取得に失敗しました。
                  <br />
                  {(query.error as Error).message}
                </p>
                <Button color="primary" onPress={() => void query.refetch()}>
                  再読み込み
                </Button>
              </div>
            ) : query.isPending ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="読み込み中..." />
              </div>
            ) : (
              <DataTable
                table={table}
                isLoading={query.isFetching}
                emptyMessage="取引先がありません"
                scrollRef={scrollRef}
              />
            )}
          </div>
        </section>

        <Paginator
          total={total}
          pageSize={PARTNERS_PAGE_SIZE}
          page={params.page}
          onChange={(page) => update({ page })}
        />
      </div>

      <EdgeScrollButton
        side="left"
        isDisabled={!canScrollLeft}
        onPress={() => scrollBy(-1)}
      />
      <EdgeScrollButton
        side="right"
        isDisabled={!canScrollRight}
        onPress={() => scrollBy(1)}
      />

      <PartnerCreateDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          showSuccess(`「${name}」を登録しました。`);
          void invalidate();
        }}
      />

      <SingleFieldEditDialog
        target={editTarget}
        onClose={() => setEditing(null)}
        onSave={(value) =>
          editing
            ? save(editing.partner, { [editing.field.key]: value } as PartnerInput)
            : Promise.resolve(null)
        }
      />

      <ConfirmDialog
        isOpen={toggling != null}
        title={toggling?.disabled ? "取引の再開" : "取引の停止"}
        message={
          toggling?.disabled
            ? `「${toggling.partner_name}」を取引中に戻します。得意先の選択候補に再び表示されます。`
            : `「${toggling?.partner_name}」を取引停止にします。\n得意先の選択候補から外れますが、過去の見積・案件はそのまま残ります。`
        }
        confirmLabel={toggling?.disabled ? "取引を再開" : "取引を停止"}
        isBusy={toggle.isPending}
        onClose={() => setToggling(null)}
        onConfirm={() => {
          if (toggling) toggle.mutate(toggling);
        }}
      />

      <ConfirmDialog
        isOpen={togglingRole != null}
        title={
          togglingRole
            ? `${PARTNER_ROLE_LABEL[togglingRole.role]}として使う設定の変更`
            : ""
        }
        message={
          togglingRole == null
            ? ""
            : togglingRole.partner[togglingRole.role]
              ? `「${togglingRole.partner.partner_name}」を${PARTNER_ROLE_LABEL[togglingRole.role]}として使わない設定にします。\n${PARTNER_ROLE_LABEL[togglingRole.role]}の選択候補から外れますが、過去の見積・案件はそのまま残ります。`
              : `「${togglingRole.partner.partner_name}」を${PARTNER_ROLE_LABEL[togglingRole.role]}として使う設定にします。\n${PARTNER_ROLE_LABEL[togglingRole.role]}の選択候補に表示されます。`
        }
        confirmLabel="変更"
        isBusy={toggleRole.isPending}
        onClose={() => setTogglingRole(null)}
        onConfirm={() => {
          if (togglingRole) toggleRole.mutate(togglingRole);
        }}
      />

      <ConfirmDialog
        isOpen={deleting != null}
        title="取引先の削除"
        message={`「${deleting?.partner_name}」を削除します。\nこの操作は取り消せません。よろしいですか？\n\n見積・案件で使われている取引先は削除できません。取引を止めただけなら「取引停止」にしてください。`}
        confirmLabel="削除"
        isDanger
        isBusy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      />
    </div>
  );
}
